import { ClientData } from '../../types';
import { filterProfileSettings, profileSettingsOptions, visibleProfileSettingsItems } from '../../utils';

export interface ProjectSettingsFieldMeta {
  field: keyof ClientData;
  title: string;
  description: string;
  options: string[];
  // Step-specific "coming soon" flag (notificationFormat today).
  disabled?: boolean;
}

// Per-field title/description/options metadata, in the same order as
// ClientDataDialog's stepper. Moved out of the dialog's `getCurrentStepData`
// switch (COM-638 task 3 review carry-forward) so both the dialog and the
// standalone settings page read the same source instead of the page
// re-declaring the same five titles/descriptions.
export const PROJECT_SETTINGS_FIELDS: ProjectSettingsFieldMeta[] = [
  {
    field: 'companyType',
    options: profileSettingsOptions.companyType,
    title: 'Company Type / Business Activity',
    description: 'Select what describes your company:',
  },
  {
    field: 'jurisdictions',
    options: profileSettingsOptions.jurisdictions,
    title: 'Jurisdiction',
    description: 'Specify in which countries/regions you operate:',
  },
  {
    field: 'topics',
    options: profileSettingsOptions.topics,
    title: 'Topics of Interest',
    description: 'Select topics you want to monitor:',
  },
  {
    field: 'documentTypes',
    options: profileSettingsOptions.documentTypes,
    title: 'Document Types',
    description: 'Specify which types of publications interest you:',
  },
  {
    field: 'notificationFormat',
    options: profileSettingsOptions.notificationFormat,
    title: 'Notification Format (SOON)',
    description: 'How you want to receive notifications:',
    disabled: true,
  },
];

// COM-638/COM-637: per Confluence 700186650 the "Notification Format" step
// must be hidden in the first implementation. This is the single source
// both ClientDataDialog (stepper) and SettingsPage (all-blocks-at-once) read
// to decide which field blocks to render, so the two surfaces cannot drift
// on what is visible. `notificationFormat` is filtered out here, not deleted
// from PROJECT_SETTINGS_FIELDS above — the field itself stays in ClientData,
// profileSettingsItems, the save payload and the DB.
export const VISIBLE_PROJECT_SETTINGS_FIELDS: ProjectSettingsFieldMeta[] = PROJECT_SETTINGS_FIELDS.filter(
  ({ field }) => visibleProfileSettingsItems.includes(field)
);

// COM-638/COM-637: the exact copy the spec requires when a user tries to
// advance past (wizard) or save (settings page) a visible field that has
// neither a predefined selection nor a custom option.
export const EMPTY_FIELD_ERROR_MESSAGE = 'Please select at least one option or add a custom one';

// Returns the visible fields (see `visibleProfileSettingsItems`) that have
// no predefined selection AND no custom option — i.e. the fields that would
// fail validation. Shared by ClientDataDialog's per-step Next/Save check and
// SettingsPage's whole-form Save check so both apply the identical rule.
export const findEmptyVisibleFields = (
  clientData: ClientData,
  customOptions: ClientData
): (keyof ClientData)[] =>
  visibleProfileSettingsItems.filter(
    (field) => clientData[field].length === 0 && customOptions[field].length === 0
  );

// Combines the predefined selections (checkboxes) with the custom options
// (free-text additions) for every ClientData field. Moved verbatim out of
// ClientDataDialog's handleSave/handleCheckboxChange, which duplicated this
// merge inline.
export const combineClientData = (
  clientData: ClientData,
  customOptions: ClientData
): ClientData => {
  const filteredData = filterProfileSettings(clientData);
  return {
    companyType: [...filteredData.companyType, ...customOptions.companyType],
    jurisdictions: [...filteredData.jurisdictions, ...customOptions.jurisdictions],
    topics: [...filteredData.topics, ...customOptions.topics],
    documentTypes: [...filteredData.documentTypes, ...customOptions.documentTypes],
    notificationFormat: [...filteredData.notificationFormat, ...customOptions.notificationFormat],
  };
};
